<template>
  <v-container fluid>
    <el-overlay-loading :loading="pageloading" />
    <h3 class="mb-5">Nekilnojamasis turtas</h3>

    <template v-if="showTurtasTeise">
      <el-checkbox
        class="mb-3"
        :label="turtasTeiseLabel"
        :tooltip="`${ntrTooltipPrefix} Turtas. ${turtasTeiseLabel}`"
        :readonly="readonlyTurtasTeise"
        v-model="formData.turtasTeise"
        @click.native="!readonlyTurtasTeise ? checkData() : null"
      />
    </template>

    <prasymas-turtas-search
      v-if="showTurtasSearch"
      :key="`turtassearch-${this.turtasRefresh}`"
      :pageloading="pageloading"
      :turtas-teise="formData.turtasTeise"
      :turtas-length="turtasLength"
      @updateTurtasList="updateTurtasList"
    />

    <prasymas-turtas-pirmenybe-search
      v-if="showTurtasPirmenybeSearch"
      :key="`turtaspirmenybesearch-${this.turtasRefresh}`"
      :pageloading="pageloading"
      :turtas-teise="formData.turtasTeise"
      :turtas-length="turtasLength"
      @updateTurtasList="updateTurtasList"
    />

    <v-data-table
      v-if="showTurtasTable"
      :class="tableClass"
      :headers="filteredHeaders"
      :items="itemsList"
      :loading="loading"
      :search="search"
      :item-class="rowClass"
      :footer-props="{
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageText: 'Rodyti po:',
        itemsPerPageAllText: 'visi',
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :hide-default-footer="hideFooter"
      :items-per-page="pageSize"
      :expanded.sync="formData.turtai"
      :group-by="groupBy"
      :show-group-by="false"
    >
      <template #[`group.header`]="{ headers, items }">
        <td :colspan="headers.length">
          {{ items[0].grupe }}
        </td>
      </template>
      <template #[`item.tur_registras`]="{ item }">
        <div class="nowrap">{{ item.tur_registras }}</div>
      </template>
      <template #[`item.tur_identifikatorius`]="{ item }">
        <div class="nowrap">{{ item.tur_identifikatorius }}</div>
      </template>
      <template #[`item.tur_plotas_format`]="{ item }">
        <template v-if="item.tur_plotas">
          <div class="nowrap">
            {{ item.tur_plotas_format }}
          </div>
          {{ item.tur_plotas_matas }}
        </template>
      </template>
      <template #[`item.rusis`]="{ item }">
        <v-row dense no-gutters>
          <v-col cols="12" md="12">
            {{ item.tur_ntr_rusis }}
          </v-col>
          <v-col cols="12" md="12">
            <span class="text-gray">{{ item.tur_ntr_adresas }}</span>
          </v-col>
        </v-row>
      </template>
      <template #[`item.actions`]="{ item }">
        <el-table-action
          :item="item"
          @editItem="editItem(item)"
          @viewItem="editItem(item)"
          @deleteItem="deleteItem(item)"
          @reloadItem="reloadItem(item)"
        />
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td class="turtas-expanded pb-4"></td>
        <td :colspan="headers.length - 1" class="turtas-expanded pb-4">
          <template v-if="item.showPastabos">
            <v-row dense no-gutters class="pt-1 pb-2">
              <v-col cols="12" md="12" class="bold-label">
                Pastabos:
              </v-col>
              <v-col
                v-for="(klaida, klaidaIndex) in item.tur_klaidos"
                :key="`klaida${turtasRefresh}-${klaidaIndex}`"
                cols="12"
                md="12"
                class="turtas-pastaba-error mb-1"
              >
                {{ klaida }}
              </v-col>
              <v-col
                v-for="(ispejimas, ispejimasIndex) in item.tur_ispejimai"
                :key="`ispejimas${turtasRefresh}-${ispejimasIndex}`"
                cols="12"
                md="12"
                class="turtas-pastaba-warning"
              >
                {{ ispejimas }}
              </v-col>
            </v-row>
            <v-divider />
          </template>

          <div class="dataContainer">
            <template v-if="!item.turtines_teises">
              <v-row dense no-gutters>
                <v-col cols="12" md="3" class="bold-label pt-3">
                  NT dalis<el-tooltip
                    inline
                    :tooltip="`${ntrTooltipPrefix} Turtas. NT dalis`"
                    :tooltip-key="`ntdalis-${item.tur_id}`"
                  />
                </v-col>
                <v-col cols="12" md="9">
                  <div
                    v-for="dalis in item.dalys"
                    :key="`dalisdiv-${item.tur_id}-${dalis.tda_id}`"
                  >
                    <el-checkbox
                      :key="`dalis-${item.tur_id}-${dalis.tda_id}`"
                      :label="
                        `${dalis.tda_pavadinimas} ${
                          dalis.tda_dokumentas
                            ? ', ' + dalis.tda_dokumentas
                            : ''
                        }`
                      "
                      :sup-label="
                        `${
                          item.dalys_pazymetos.includes(dalis.tda_id)
                            ? dalis.pastabos
                            : ''
                        }`
                      "
                      :check-value="dalis.tda_id"
                      :readonly="item.readonly || isPrasymasPirmenybe"
                      v-model="item.dalys_pazymetos"
                      @click.native="checkData()"
                    />
                  </div>
                </v-col>
              </v-row>
            </template>

            <template v-if="item.turtines_teises">
              <v-row dense no-gutters class="mb-2">
                <v-col cols="12" md="3" class="bold-label pt-4">
                  {{ item.turtasTeiseTitle
                  }}<el-tooltip
                    inline
                    :tooltip="
                      `${ntrTooltipPrefix} Turtas. ${item.turtasTeiseTitle}`
                    "
                    :tooltip-key="`turtasteise-${item.tur_id}`"
                  />
                </v-col>
                <v-col cols="12" md="9">
                  <div
                    v-for="teise in item.teises"
                    :key="`teisediv-${item.tur_id}-${teise.tte_id}`"
                  >
                    <v-row dense no-gutters>
                      <v-col cols="12" md="3" v-if="!isPrasymasSubnuoma">
                        <el-checkbox
                          :key="`dalis-${item.tur_id}-${teise.tte_id}`"
                          :label="`${teise.tda_pavadinimas}`"
                          :sup-label="
                            `${
                              item.teises_pazymetos.includes(teise.tte_id)
                                ? teise.pastabos
                                : ''
                            }`
                          "
                          :check-value="teise.tte_id"
                          :readonly="item.readonly"
                          v-model="item.teises_pazymetos"
                          @click.native="checkData()"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="3"
                        class="mt-3"
                        v-if="isPrasymasSubnuoma"
                      >
                        {{ teise.tda_pavadinimas }}
                      </v-col>
                      <v-col cols="12" md="9">
                        <div class="text-gray mt-3">
                          <div v-if="teise.tda_pradzia">
                            Galioja nuo: {{ teise.tda_pradzia }}
                          </div>
                          <div v-if="teise.tda_terminas">
                            Terminas: {{ teise.tda_terminas }}
                          </div>
                          <div v-if="teise.tda_dokumentas">
                            {{ teise.tda_dokumentas }}
                          </div>
                        </div>
                        <div v-if="teise.isnuomota_dalis">
                          {{ isnuomotaDalisPavadinimas(teise.tda_pavadinimas) }}
                          {{ teise.isnuomota_dalis }}
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </template>

            <template
              v-if="
                (isPrasymasNuoma ||
                  isPrasymasPanauda ||
                  isPrasymasSubnuoma ||
                  isPrasymasPreliminari) &&
                  isPrasymasIregistravimas
              "
            >
              <v-divider class="my-3" />
              <v-row
                dense
                v-if="
                  isPrasymasNuoma || isPrasymasSubnuoma || isPrasymasPreliminari
                "
              >
                <v-col cols="12" md="6">
                  <el-money-field
                    :label="isnuomotaSumaLabel"
                    :tooltip="
                      `${ntrTooltipPrefix} Turtas. ${isnuomotaSumaLabel}`
                    "
                    :need="!item.readonly"
                    :readonly="item.readonly"
                    :key="`tur_suma${item.tur_id}`"
                    :validate-key="`tur_suma${item.tur_id}`"
                    hide-details="auto"
                    :precision="2"
                    v-model="item.tur_suma"
                    @blur="checkData()"
                  />
                </v-col>
                <v-col cols="12" md="3" v-if="isPrasymasPreliminari">
                  <el-checkbox
                    class="mt-4 ml-4"
                    label="Įskaičiuotas PVM"
                    :tooltip="`${ntrTooltipPrefix} Turtas. Įskaičiuotas PVM`"
                    :readonly="item.readonly"
                    v-model="item.tur_iskaiciuotas_pvm"
                    @click.native="checkData()"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col
                  cols="12"
                  md="6"
                  class="pr-5"
                  v-if="item.tur_plotas && !isPrasymasPreliminari"
                >
                  <el-money-field
                    :label="isnuomotasPlotasLabel"
                    :tooltip="
                      `${ntrTooltipPrefix} Turtas. ${isnuomotasPlotasLabel}`
                    "
                    :need="!item.readonly && item.tur_plotas !== null"
                    :readonly="item.readonly || !item.tur_plotas"
                    hide-details="auto"
                    :precision="item.tur_plotas_precision"
                    :key="`tur_plotas_isnuomotas${item.tur_id}`"
                    :validate-key="`tur_plotas_isnuomotas${item.tur_id}`"
                    v-model="item.tur_plotas_isnuomotas"
                    @blur="checkData()"
                  >
                    <template slot="append-outer">
                      <div
                        class="plotas-matas mt-2 nowrap no-color-label small-label"
                      >
                        {{ item.tur_plotas_matas }}
                      </div>
                    </template>
                  </el-money-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  v-if="
                    item.tur_plotas_pazymetas &&
                      (isPrasymasNuoma ||
                        isPrasymasPanauda ||
                        isPrasymasSubnuoma ||
                        isPrasymasPreliminari)
                  "
                >
                  <el-money-field
                    :label="priklausantisPlotasLabel"
                    :tooltip="
                      `${ntrTooltipPrefix} Turtas. ${priklausantisPlotasLabel}`
                    "
                    :readonly="true"
                    hide-details="auto"
                    :precision="item.tur_plotas_precision"
                    :key="`tur_plotas_pazymetas${item.tur_id}`"
                    :validate-key="`tur_plotas_pazymetas${item.tur_id}`"
                    v-model="item.tur_plotas_pazymetas"
                    @blur="checkData()"
                  >
                    <template slot="append-outer">
                      <div
                        class="plotas-matas mt-2 nowrap no-color-label small-label"
                      >
                        {{ item.tur_plotas_matas }}
                      </div>
                    </template>
                  </el-money-field>
                </v-col>
              </v-row>

              <v-row dense class="mt-3">
                <v-col cols="12" md="12">
                  <el-textarea
                    :readonly="item.readonly"
                    :label="isnuomotasPlotasPastabaLabel"
                    :tooltip="
                      `${ntrTooltipPrefix} Turtas. ${isnuomotasPlotasPastabaLabel}`
                    "
                    :tooltip-key="`-${item.tur_id}`"
                    hide-details="auto"
                    counter="1000"
                    :key="`tur_plotas_pastaba${item.tur_id}`"
                    :validate-key="`tur_plotas_pastaba${item.tur_id}`"
                    v-model="item.tur_plotas_pastaba"
                    @blur="checkData()"
                  />
                </v-col>
              </v-row>

              <v-row
                dense
                no-gutters
                v-if="item.reikalingas_bendraturciu_patvirtinimas"
              >
                <v-col cols="12" md="12">
                  <el-checkbox
                    :label="bendraturciuPatvirtinimasLabel"
                    :tooltip="
                      `${ntrTooltipPrefix} Turtas. ${bendraturciuPatvirtinimasTooltip}`
                    "
                    :readonly="item.readonly"
                    :key="`tur_bendraturciu_patvirtinimas${item.tur_id}`"
                    :validate-key="
                      `tur_bendraturciu_patvirtinimas${item.tur_id}`
                    "
                    v-model="item.tur_bendraturciu_patvirtinimas"
                    @click.native="saveTurtas(item, true)"
                  />
                </v-col>
              </v-row>
            </template>
          </div>
        </td>
      </template>
      <template slot="loading">
        Kraunasi...
      </template>
      <template slot="no-results">
        Duomenų nėra
      </template>
      <template slot="no-data">
        Duomenų nėra
      </template>
      <template #[`footer.page-text`]="props">
        Rodoma {{ props.pageStart }}-{{ props.pageStop }} iš
        {{ props.itemsLength }}
      </template>
    </v-data-table>

    <modal-delete
      title="Šalinti turtą?"
      :text="`Ar norite pašalinti turtą <strong>${modalDeleteVardas}</strong>?`"
      v-model="showDelete"
      :item="modalDeleteItem"
      @confirmDelete="confirmDelete"
    />

    <v-footer class="form-footer" v-if="!isReadonly && showTurtasFooter">
      <v-spacer></v-spacer>
      <el-button
        primary
        @click.native="checkErrorsClick()"
        :loading="loadingTikrinti"
        >Tikrinti</el-button
      >
    </v-footer>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import PrasymasTurtasSearch from './RegPrasymasTurtasSearch'
import PrasymasTurtasPirmenybeSearch from './RegPrasymasTurtasPirmenybeSearch'
import ModalDelete from '../modals/ModalDelete'
import unsavedData from '@/mixins/unsavedData'
import checkErrors from '@/mixins/checkErrors'
import rowClass from '@/mixins/rowClass'
import busena from '@/mixins/busenaNtr'
import userRole from '@/mixins/userRole'
import math from '@/mixins/math'
import tooltipNtr from '@/mixins/tooltipNtr'

export default {
  name: 'RegPrasymasTurtas',
  components: {
    PrasymasTurtasSearch,
    PrasymasTurtasPirmenybeSearch,
    ModalDelete,
  },
  props: {
    readonlyForm: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  mixins: [
    busena,
    rowClass,
    userRole,
    checkErrors,
    unsavedData,
    math,
    tooltipNtr,
  ],
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      pageloading: true,
      loading: true,
      search: null,
      showDelete: false,
      modalDeleteVardas: '',
      modalDeleteItem: null,
      modalDeleteRole: null,
      turtai: [],
      salys: [],
      turtasRefresh: 0,
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      formData: {
        turtai: [],
        turtasTeise: false,
      },
    }
  },
  computed: {
    ...mapGetters(['prasymasID']),
    headers() {
      return [
        {
          text: 'Eil. Nr.',
          align: 'start',
          value: 'eilnr',
          width: 50,
        },
        {
          text: 'Registro numeris',
          align: 'start',
          value: 'tur_registras',
        },
        {
          text: 'Unikalus numeris',
          align: 'start',
          value: 'tur_identifikatorius',
        },
        {
          text: 'Plotas',
          align: 'start',
          value: 'tur_plotas_format',
          hide: !this.showPlotas,
        },
        {
          text: 'Naudojimo paskirtis',
          align: 'start',
          value: 'tur_paskirtis',
          hide: !this.showPaskirtis,
        },
        {
          text: 'Turto rūšis',
          align: 'start',
          value: 'rusis',
        },
        {
          text: 'Veiksmai',
          align: 'start',
          value: 'actions',
          hide: this.isReadonlyForm,
          width: 50,
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      return this.turtai.map((item, index) => {
        item.eilnr = `${index + 1}.`

        item.grupenr = item.tur_pasalintas ? 1 : 2
        item.grupe = item.tur_pasalintas
          ? 'Daikto, kuriam išregistruojamas juridinis faktas, duomenys:'
          : 'Daikto, kuriam įregistruojamas juridinis faktas, duomenys:'

        if (item.turtines_teises) {
          item.turtasTeiseTitle = 'Teisės duomenys'
          // if (
          //   item.teises.length > 0 &&
          //   item.teises[0].tda_pavadinimas === 'Panaudos teisė'
          // ) {
          //   item.turtasTeiseTitle = 'Panaudos teisės duomenys'
          // }
        }

        item.showPastabos =
          item.tur_klaidos.length > 0 || item.tur_ispejimai.length > 0

        item.tur_plotas_format = item.tur_plotas
          ? item.tur_plotas.replace(item.tur_plotas_matas, '')
          : null

        item.tur_plotas_precision = 2
        if (item.tur_plotas_matas === 'ha') {
          item.tur_plotas_precision = 4
        }

        item.readonly = item.tur_pasalintas || this.isReadonlyForm
        item.canEdit = false
        item.canView = false
        item.canDelete =
          !this.isReadonlyForm &&
          (!item.readonly ||
            (this.isPrasymasSeima &&
              this.isPrasymasIsregistravimas &&
              this.isGrazintas)) &&
          !(this.isPrasymasNuoma && !this.isPrasymasIregistravimas) &&
          !(this.isPrasymasPanauda && !this.isPrasymasIregistravimas)
        item.canReload = item.tur_pasikeite

        return item
      })
    },
    saveItemsList() {
      return this.itemsList.map(item => {
        return {
          tur_id: item.tur_id,
          dalys_pazymetos: item.dalys_pazymetos,
          teises_pazymetos: item.teises_pazymetos,
          tur_suma: item.tur_suma,
          tur_plotas_isnuomotas: item.tur_plotas_isnuomotas,
          tur_plotas_pastaba: item.tur_plotas_pastaba,
          tur_iskaiciuotas_pvm: item.tur_iskaiciuotas_pvm,
        }
      })
    },
    groupBy() {
      return this.isPrasymasPerkelimas ? 'grupenr' : null
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    isReadonly() {
      return this.readonly
    },
    isReadonlyForm() {
      return (
        this.readonlyForm ||
        (this.isPrasymasNuoma && !this.isPrasymasIregistravimas) ||
        (this.isPrasymasPanauda && !this.isPrasymasIregistravimas)
      )
    },
    showTurtasSearch() {
      return (
        !this.isReadonlyForm &&
        !(this.isGrazintas && !this.roleNotaras) &&
        !(
          this.isPrasymasSeima &&
          this.isPrasymasIsregistravimas &&
          this.yraTurtas
        ) &&
        !(this.isPrasymasNuoma && !this.isPrasymasIregistravimas) &&
        !(this.isPrasymasPanauda && !this.isPrasymasIregistravimas) &&
        !this.isPrasymasPirmenybe
      )
    },
    showTurtasPirmenybeSearch() {
      return (
        this.isPrasymasPirmenybe &&
        !this.isReadonlyForm &&
        !(this.isGrazintas && !this.roleNotaras)
      )
    },
    tableClass() {
      return 'prasymas-turtas-table'
    },
    isnuomotaSumaLabel() {
      if (this.isPrasymasSubnuoma) {
        return 'Subnuomos suma per mėnesį, EUR'
      }
      if (this.isPrasymasPreliminari) {
        return 'Pardavimo kaina, EUR'
      }
      return 'Nuomos suma per mėnesį, EUR'
    },
    isnuomotasPlotasLabel() {
      if (this.isPrasymasPanauda) {
        return 'Suteikiamos panaudai objekto dalies plotas'
      }
      if (this.isPrasymasSubnuoma) {
        return 'Subnuomotas plotas'
      }
      return 'Išnuomotas plotas'
    },
    priklausantisPlotasLabel() {
      if (this.isPrasymasSubnuoma) {
        return 'Maksimalus leistinas subnuomoti plotas'
      }
      if (this.isPrasymasPreliminari) {
        return 'Priklausantis dalies plotas'
      }
      return 'Priklausantis dalies(-ių) plotas'
    },
    isnuomotasPlotasPastabaLabel() {
      if (this.isPrasymasPanauda) {
        return 'Suteikiamos panaudai objekto dalies ploto aprašymas'
      }
      if (this.isPrasymasSubnuoma) {
        return 'Subnuomoto ploto pastaba'
      }
      if (this.isPrasymasPreliminari) {
        return 'Parduodamo nekilnojamojo daikto (dalies) aprašymas'
      }
      return 'Išnuomoto ploto pastaba'
    },
    showTurtasTeise() {
      return (
        (this.isPrasymasSeima && this.isPrasymasIregistravimas) ||
        (this.isPrasymasSeima && this.isPrasymasPerkelimas && this.yraTurtas)
      )
    },
    turtasTeiseLabel() {
      if (this.isPrasymasPerkelimas) {
        return 'Šeimos turtas perkeliamas į turtinę nuomos / panaudos / subnuomos teisę'
      }
      return 'Šeimos turtas registruojamas į turtinę nuomos / panaudos / subnuomos teisę'
    },
    readonlyTurtasTeise() {
      return (
        this.isReadonlyForm ||
        (this.isPrasymasSeima &&
          this.isPrasymasIregistravimas &&
          this.turtai.length > 0) ||
        (this.isPrasymasSeima &&
          this.isPrasymasPerkelimas &&
          this.turtai.length > 1)
      )
    },
    turtasLength() {
      return this.itemsList.length
    },
    yraTurtas() {
      return this.turtasLength > 0
    },
    showTurtasTable() {
      return (
        !this.isPrasymasSeima ||
        (this.isPrasymasSeima && this.isPrasymasIregistravimas) ||
        (this.isPrasymasSeima && this.isPrasymasPerkelimas && this.yraTurtas) ||
        (this.isPrasymasSeima &&
          this.isPrasymasIsregistravimas &&
          this.yraTurtas)
      )
    },
    showTurtasFooter() {
      return (
        !this.isPrasymasSeima ||
        (this.isPrasymasSeima && this.isPrasymasIregistravimas) ||
        (this.isPrasymasSeima && this.isPrasymasPerkelimas && this.yraTurtas) ||
        (this.isPrasymasSeima &&
          this.isPrasymasIsregistravimas &&
          this.yraTurtas)
      )
    },
    showPlotas() {
      return (
        this.isPrasymasNuoma ||
        this.isPrasymasPanauda ||
        this.isPrasymasSubnuoma ||
        this.isPrasymasPreliminari
      )
    },
    showPaskirtis() {
      return (
        this.isPrasymasNuoma ||
        this.isPrasymasPanauda ||
        this.isPrasymasSubnuoma ||
        this.isPrasymasPreliminari
      )
    },
    bendraturciuPatvirtinimasLabel() {
      if (this.isPrasymasPanauda) {
        return this.$t(
          'Aš patvirtinu, kad kartu su statiniu suteikiama panaudai jam eksploatuoti reikalinga žemės sklypo dalis, todėl sutikimas neprisegamas'
        )
      }
      return this.$t(
        'Aš patvirtinu, kad kartu su statiniu išnuomojama jam eksploatuoti reikalinga žemės sklypo dalis, todėl sutikimas neprisegamas'
      )
    },
    bendraturciuPatvirtinimasTooltip() {
      return 'Bendraturčių patvirtinimas'
    },
  },
  created() {
    this.pageloading = true
    this.getData()
  },
  methods: {
    getData(silent = false) {
      if (!silent) {
        this.loading = true
        this.loadingTikrinti = true
      }
      this.$axios
        .get(`ntrpaslaugos/prasymas/${this.prasymasID}/turtas`)
        .then(res => {
          this.turtai = res.data.turtai
          this.formData.turtai = this.itemsList
          this.formData.turtasTeise = res.data.pra_turtas_teise
          // this.turtasRefresh++
          this.$nextTick(() => {
            this.updateErrors()
            this.initialForm()
          })
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.loadingTikrinti = false
          this.pageloading = false
        })
    },
    saveData(toRoute, afterSave) {
      if (toRoute) {
        this.goToRoute()
        return
      }
      if (this.isReadonlyForm) {
        return
      }
      this.$validator.validateAll().then(res => {
        if (res) {
          // this.loading = true

          const data = {
            pra_id: this.prasymasID,
            pra_turtas_teise: this.formData.turtasTeise,
            turtai: this.saveItemsList,
          }

          this.$axios
            .post(`ntrpaslaugos/prasymas/${this.prasymasID}/turtas`, data)
            .then(res => {
              this.updateTurtasList(true)
            })
            .catch(err => {
              window._VMA.$emit('DATA_ERROR', err.response.data.message)
            })
            .finally(() => {
              this.loading = false
            })
        } else {
          window._VMA.$emit('FORMAT_ERROR')
          this.$root.$emit('cancelTabNavigation')
        }
      })
    },
    cancelData() {
      this.getData()
    },
    checkErrorsClick() {
      this.loadingTikrinti = true
      this.checkNtrErrors()
    },
    deleteItem(item) {
      this.modalDeleteVardas = item.tur_identifikatorius
      this.modalDeleteItem = Object.assign({}, item)
      this.showDelete = true
    },
    confirmDelete(item) {
      this.confirmDeleteTurtas(item)
    },
    confirmDeleteTurtas(item) {
      this.$axios
        .delete(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/${item.tur_id}`
        )
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.updateTurtasList()
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
        })
    },
    reloadItem(item) {
      this.$axios
        .post(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/${item.tur_id}/atnaujinimas`
        )
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
        })
        .catch(err => {
          window._VMA.$emit('DATA_ERROR', err.response.data.message)
        })
        .finally(() => {
          this.loading = false
          this.pageloading = false
          this.updateTurtasList()
        })
    },
    updateTurtasList(silent = false) {
      this.$root.$emit('reloadPrasymasTab')
      this.getData(silent)
    },
    rowClass(item) {
      if (
        item.tur_pasalintas &&
        this.isPrasymasSeima &&
        this.isPrasymasPerkelimas
      ) {
        return `row-gray row-deleted`
      }
      return 'row-gray'
    },
    isnuomotaDalisPavadinimas(teisePavadinimas) {
      if (teisePavadinimas === 'Subnuomos teisė') {
        return 'Subnuomota NT dalis:'
      }
      if (teisePavadinimas === 'Panaudos teisė') {
        return 'Panaudai suteikta NT dalis:'
      }
      return 'Išnuomota NT dalis:'
    },
  },
}
</script>
<style lang="scss" scoped>
.dataContainer {
  max-width: 1000px;
}
.turtas-adresas {
  color: #888888;
}
$errorHigh: #f11a1a;
$errorMiddle: #e4800d;
$errorLow: #03aa03;

.turtas-pastaba-error {
  color: $errorHigh;
}
.turtas-pastaba-warning {
  color: $errorMiddle;
}
.turtas-expanded {
  box-shadow: none;
  // background: rgb(248, 248, 248);
  background: #fff;
}
</style>
